import { useEffect, useState } from 'preact/hooks';
import { platformPayments, PaymentEventRow, formatCents } from './adminApi';

export function PaymentEventsPage() {
  const [rows, setRows] = useState<PaymentEventRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<PaymentEventRow | null>(null);
  const [selectedRaw, setSelectedRaw] = useState<string>('');

  const [typeFilter, setTypeFilter] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);
  const [unprocessedOnly, setUnprocessedOnly] = useState(false);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const r = await platformPayments.query({
        eventTypePrefix: typeFilter.trim() || undefined,
        failedOnly: failedOnly || undefined,
        unprocessedOnly: unprocessedOnly || undefined,
        limit: 100,
      });
      setRows(r.rows || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openRow(r: PaymentEventRow) {
    setSelected(r);
    setSelectedRaw('loading…');
    try {
      const detail = await platformPayments.get(r.id);
      setSelectedRaw(detail.rawPayload || '{}');
    } catch (e) {
      setSelectedRaw(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  return (
    <div>
      <h1 style={{ marginTop: 0 }}>Payment events</h1>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Webhook events received from consumer-payment gateways (per-org Stripe accounts, etc.). Click a row to see raw payload.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
        style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}
      >
        <input
          placeholder="Event type (e.g. charge.*, checkout.session.completed)"
          value={typeFilter}
          onInput={(e) => setTypeFilter((e.target as HTMLInputElement).value)}
          style={{ flex: 1, minWidth: 260 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={failedOnly}
            onChange={(e) => setFailedOnly((e.target as HTMLInputElement).checked)}
          />
          <span style={{ fontSize: 13, color: 'var(--admin-text-dim)' }}>Failed only</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={unprocessedOnly}
            onChange={(e) => setUnprocessedOnly((e.target as HTMLInputElement).checked)}
          />
          <span style={{ fontSize: 13, color: 'var(--admin-text-dim)' }}>Unprocessed only</span>
        </label>
        <button class="primary" type="submit" disabled={loading}>
          Apply
        </button>
      </form>

      {err && <div class="error-banner">{err}</div>}

      <table class="admin-table">
        <thead>
          <tr>
            <th>Received</th>
            <th>Gateway</th>
            <th>Event type</th>
            <th>Org</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
            <th>Processed</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => openRow(r)} style={{ cursor: 'pointer' }}>
              <td class="mono">{new Date(r.receivedAt).toLocaleString()}</td>
              <td class="mono">{r.gateway}</td>
              <td class="mono">{r.eventType}</td>
              <td>{r.orgName || '—'}</td>
              <td style={{ textAlign: 'right' }} class="mono">
                {r.amountCents ? formatCents(r.amountCents) : '—'}
              </td>
              <td>
                {r.processedAt ? (
                  <span class="badge success">ok</span>
                ) : r.processError ? (
                  <span class="badge failure">failed</span>
                ) : (
                  <span class="badge" style={{ background: 'var(--admin-surface-raised)', color: 'var(--admin-text-dim)' }}>
                    pending
                  </span>
                )}
              </td>
              <td style={{ color: r.processError ? 'var(--admin-danger)' : 'inherit' }}>
                {r.processError || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!loading && rows.length === 0 && (
        <p style={{ color: 'var(--admin-text-dim)' }}>No payment events.</p>
      )}

      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--admin-surface)',
              border: '1px solid var(--admin-border)',
              borderRadius: 12,
              padding: 24,
              maxWidth: 900,
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0 }}>
                {selected.eventType}
              </h2>
              <button onClick={() => setSelected(null)}>Close</button>
            </div>
            <p class="mono" style={{ color: 'var(--admin-text-dim)' }}>
              {selected.gateway} · {new Date(selected.receivedAt).toLocaleString()} ·{' '}
              {selected.orgName || '(no org)'}
            </p>
            <h3>Raw payload</h3>
            <pre>{prettyJSON(selectedRaw)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function prettyJSON(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
